/* eslint-disable max-classes-per-file */
import type { ResponseInterface } from "@/libs/server/goApiServer/common/commonInterface";
import type {
  TicketAvailableMeetingPoint,
  TicketAvailableQuestion,
  TicketAvailableTermsAndCondition,
} from "@/libs/server/goApiServer/experience/ExperienceServiceInterface/getEventTicketAvailableInterfaces";

import { CorporateDisCountPresenter } from "../corporate/corporateServiceInterfaces/corporateInterface";

export interface RequestAttractionDetailInterface {
  ticketId: number;
  ticket: {
    quantity: number;
    key: string;
  }[];
}

export interface TicketData {
  id: number;
  name: string;
  ticket_name: string;
  ticket_slug: string;
  tier: string;
  mobile_number: string;
  photo_cover_path: string;
  expired: string;
  guestQuestions: TicketAvailableQuestion[];
  meetingPoint: TicketAvailableMeetingPoint[];
  freeCancellation: boolean;
  cancellationPolicy: string;
  photoPath: string;
  howToUse: string | null;
  termsAndConditions: TicketAvailableTermsAndCondition[] | null;
  redeemStart: string;
  redeemEnd: string;
  prices: Price[];
  totalPrice: number;
  totalAmount: number;
  totalPax: {
    type: string;
    amount: number;
  }[];
  currency: string;
  keySearchTimeExpire: number;
  area_slug: string;
  city_slug: string;
  country_slug: string;
  ticket_type: string;
}

export interface Price {
  id: number;
  type: string;
  nameEn: string;
  nameTh: string;
  price: number;
  originalPrice: number;
  exclusivePrice: number;
  currency: string;
  amount: number;
  netPrice: number;
  expirationDate: number;
}

export class ResponsePreAllocateData {
  id: number;

  name: string;

  ticket_name: string;

  ticket_slug: string;

  tier: string;

  photo_cover_path: string;

  mobile_number: string;

  expired: string;

  guestQuestions: TicketAvailableQuestion[];

  meetingPoint: TicketAvailableMeetingPoint[];

  freeCancellation: boolean;

  cancellationPolicy: string;

  photoPath: string;

  howToUse: string | null;

  termsAndConditions: TicketAvailableTermsAndCondition[] | null;

  redeemStart: string;

  redeemEnd: string;

  prices: Price[];

  totalPrice: number;

  totalAmount: number;

  totalPax: {
    type: string;
    amount: number;
  }[];

  currency: string;

  keySearchTimeExpire: number;

  country_slug: string;

  city_slug: string;

  area_slug: string;

  ticket_type: string;

  constructor(data: TicketData) {
    this.cancellationPolicy = data?.cancellationPolicy || "";
    this.name = data.name || "";
    this.currency = data?.currency || "";
    this.totalPrice = data?.totalPrice || 0;
    this.expired = data?.expired || "";
    this.photo_cover_path = data.photo_cover_path;
    this.freeCancellation = data?.freeCancellation || false;
    this.howToUse = data?.howToUse || null;
    this.id = data?.id || 0;
    this.meetingPoint = data?.meetingPoint || [];
    this.mobile_number = data?.mobile_number || "";
    this.photoPath = data?.photoPath || "";
    this.prices = data?.prices || [];
    this.guestQuestions = data?.guestQuestions || [];
    this.redeemEnd = data?.redeemEnd || "";
    this.redeemStart = data?.redeemStart || "";
    this.termsAndConditions = data?.termsAndConditions || null;
    this.ticket_name = data?.ticket_name || "";
    this.ticket_slug = data?.ticket_slug || "";
    this.tier = data?.tier || "";
    this.totalAmount = data?.totalAmount || 0;
    this.keySearchTimeExpire = data?.keySearchTimeExpire || 0;
    this.area_slug = data?.area_slug || "";
    this.city_slug = data?.city_slug || "";
    this.country_slug = data?.country_slug || "";
    this.ticket_type = data?.ticket_type || "";
    this.totalPax = data?.totalPax || [
      {
        amount: 0,
        type: "",
      },
    ];
  }
}

export class ResponsePreAllocate {
  status: boolean;

  message: string;

  data: TicketData;

  constructor(response: ResponseInterface<TicketData>) {
    this.status = response.status || false;
    this.message = response.message || "";
    this.data = new ResponsePreAllocateData(response?.data);
  }
}

export interface DataV3PerkBenefits {
  type: string;
  name: string;
  name_en: string;
  name_th: string;
  detail: string;
  detail_en: string;
  detail_th: string;
  travelling_date_end: string;
  travelling_date_start: string;
}
export interface DataV3Perk {
  isPerks: boolean;
  perkIds: number[];
  benefits: DataV3PerkBenefits[];
}
export interface TicketDataV3 extends CorporateDisCountPresenter {
  id: number;
  name: string;
  ticket_name: string;
  ticket_slug: string;
  tier: string;
  mobile_number: string;
  photo_cover_path: string;
  expired: string;
  itineraryDetail: string;
  meetingPoint: TicketAvailableMeetingPoint[];
  freeCancellation: boolean;
  cancellationPolicy: string;
  photoPath: string;
  howToUse: string | null;
  termsAndConditions: TicketAvailableTermsAndCondition[] | null;
  redeemStart: string;
  redeemEnd: string;
  prices: PriceV3[];
  totalGotherDiscount: number;
  totalGotherPrice: number;
  totalPrice: number;
  totalAmount: number;
  totalPax: {
    type: string;
    amount: number;
  }[];
  perks: DataV3Perk;
  currency: string;
  keySearchTimeExpire: number;
  area_slug: string;
  city_slug: string;
  country_slug: string;
  ticket_type: string;
  expirationDate: number;
  requiredFields: RequiredFields[];
  categories: Category[];
}

export interface QuestionsRequiredFields {
  id: string;
  name: string;
  type: string;
  isAllRequired: boolean;
  options: [];
}

export interface RequiredFields {
  groupId: string;
  groupName: string;
  questions: QuestionsRequiredFields[];
  isAllRequired: boolean;
}

export interface Category {
  name: string;
  slug: string;
}

export interface PriceV3 {
  id: number;
  type: string;
  nameEn: string;
  nameTh: string;
  price: number;
  originalPrice: number;
  currency: string;
  minPax: number;
  maxPax: number;
  amount: number;
  netPrice: number;
  expirationDate: number;
  gotherDiscount: number;
  gotherPrice: number;
  netGotherPrice: number;
}

export class ResponsePreAllocateDataV3 extends CorporateDisCountPresenter {
  id: number;

  name: string;

  ticket_name: string;

  ticket_slug: string;

  tier: string;

  photo_cover_path: string;

  mobile_number: string;

  expired: string;

  itineraryDetail: string;

  meetingPoint: TicketAvailableMeetingPoint[];

  freeCancellation: boolean;

  cancellationPolicy: string;

  photoPath: string;

  howToUse: string | null;

  termsAndConditions: TicketAvailableTermsAndCondition[] | null;

  redeemStart: string;

  redeemEnd: string;

  prices: PriceV3[];

  totalPrice: number;

  totalGotherDiscount: number;

  totalGotherPrice: number;

  totalAmount: number;

  totalPax: {
    type: string;
    amount: number;
  }[];

  perks: DataV3Perk;

  currency: string;

  keySearchTimeExpire: number;

  country_slug: string;

  city_slug: string;

  area_slug: string;

  ticket_type: string;

  expirationDate: number;

  requiredFields: RequiredFields[];

  categories: Category[];

  constructor(data: TicketDataV3) {
    super(data);
    this.cancellationPolicy = data?.cancellationPolicy || "";
    this.name = data.name || "";
    this.currency = data?.currency || "";
    this.totalPrice = data?.totalPrice || 0;
    this.totalGotherDiscount = data?.totalGotherDiscount || 0;
    this.totalGotherPrice = data?.totalGotherPrice || 0;
    this.expired = data?.expired || "";
    this.itineraryDetail = data?.itineraryDetail || "";
    this.photo_cover_path = data.photo_cover_path;
    this.freeCancellation = data?.freeCancellation || false;
    this.howToUse = data?.howToUse || null;
    this.id = data?.id || 0;
    this.meetingPoint = data?.meetingPoint || [];
    this.mobile_number = data?.mobile_number || "";
    this.photoPath = data?.photoPath || "";
    this.prices = data?.prices || [];
    this.redeemEnd = data?.redeemEnd || "";
    this.redeemStart = data?.redeemStart || "";
    this.termsAndConditions = data?.termsAndConditions || null;
    this.ticket_name = data?.ticket_name || "";
    this.ticket_slug = data?.ticket_slug || "";
    this.tier = data?.tier || "";
    this.totalAmount = data?.totalAmount || 0;
    this.keySearchTimeExpire = data?.keySearchTimeExpire || 0;
    this.area_slug = data?.area_slug || "";
    this.city_slug = data?.city_slug || "";
    this.country_slug = data?.country_slug || "";
    this.ticket_type = data?.ticket_type || "";
    this.expirationDate = data?.expirationDate || 0;
    this.perks = data?.perks || {
      isPerks: false,
      perkIds: [],
      benefits: [],
    };
    this.totalPax = data?.totalPax || [
      {
        amount: 0,
        type: "",
      },
    ];
    this.requiredFields = data?.requiredFields || [];
    this.categories = data?.categories || [];
  }
}

export class ResponsePreAllocateV3 {
  status: boolean;

  message: string;

  data: TicketDataV3;

  constructor(response: ResponseInterface<TicketDataV3>) {
    this.status = response.status || false;
    this.message = response.message || "";
    this.data = new ResponsePreAllocateDataV3(response?.data);
  }
}
